<script setup>
// Page 8 — Pricing: one flat fee, and the two things a buyer asks straight after it.
// The price used to sit alone in a 150vh panel with nothing else to decide, so this
// page now also carries what Mollie charges (and that Pandig doesn't mark it up), the
// cancellation terms, the portfolio conversation, and a low-commitment way to just ask
// a question — the objections that otherwise get answered nowhere before the signup
// form.
//
// Scroll-scrubbed over a taller sticky section (the same shape as LandingStakes,
// FeatureDeck, and LandingHow), not a once-triggered reveal on a single-viewport
// .snap-page: the price's split-flap reveal is the one number on the whole site
// that matters most, and a once-triggered animation could be scrolled straight
// past before it finished flipping into place. Tying it to scroll means it can't
// be outrun, and it reverses cleanly if you scroll back up. The heading and the fine
// print resolve from DIM rather than from nothing — a price page you can land on and
// not read is worse than one with no animation at all — but the amount itself still
// flips in from zero opacity: it's the one element whose arrival IS the beat, and it
// stays legible in the pre-rendered HTML for anyone without JavaScript.
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { MessageCircle } from "@lucide/vue";
import { gsap, ScrollTrigger, useMotion, reducedMotion, SplitText, SCRUB, DIM } from "@/lib/motion";

const { t } = useI18n();

const root = ref(null);
const headingBlock = ref(null);
const priceCard = ref(null);
const pricingAmount = ref(null);
const finePrint = ref(null);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    if (reducedMotion) return;

    gsap.set([headingBlock.value, priceCard.value, finePrint.value], { opacity: DIM, y: 24 });

    // The price itself gets one extra beat: a mechanical split-flap reveal, character
    // by character, in hard steps — the same "civic instrument" voice as the ticking
    // counters elsewhere, so the one number on this page that matters most doesn't
    // just fade in like everything else.
    const priceSplit = SplitText.create(pricingAmount.value, { type: "chars", mask: "chars" });
    gsap.set(priceSplit.chars, { opacity: 0, yPercent: 70 });

    const tl = gsap
      .timeline({ paused: true })
      .to([headingBlock.value, priceCard.value], { opacity: 1, y: 0, duration: 0.55, stagger: 0.12 })
      .to(priceSplit.chars, { opacity: 1, yPercent: 0, duration: 0.4, ease: "steps(5)", stagger: 0.05 }, "-=0.2")
      .to(finePrint.value, { opacity: 1, y: 0, duration: 0.5 }, "-=0.15");

    ScrollTrigger.create({
      trigger: root.value,
      start: "top top",
      end: "bottom bottom",
      scrub: SCRUB,
      animation: tl,
    });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <!-- Its content fades in across the section's own scroll range, so jumping to
       the section's top edge would land on a blank panel: a menu/pager jump goes
       to the settled end instead (see scrollElementIntoView in lib/scroll.js). -->
  <section
    id="prijzen"
    ref="root"
    class="relative border-t-2 border-foreground bg-card"
    :class="reducedMotion ? 'page-panel flex items-center' : 'h-[125vh] sm:h-[145vh]'"
    data-jump="settled"
    :data-page-label="t('landing.pager.pricing')"
  >
    <div :class="reducedMotion ? 'w-full' : 'page-camera flex [align-items:safe_center] overflow-y-auto lg:items-center lg:overflow-hidden'">
      <div class="mx-auto w-full max-w-6xl px-5 py-6 sm:py-14">
        <div class="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
          <div ref="headingBlock" class="max-w-xl">
            <h2 class="font-sans text-xl font-bold tracking-tight text-foreground sm:text-3xl">{{ t("landing.pricing.title") }}</h2>
            <p class="mt-2 text-xs leading-snug text-muted-foreground sm:mt-4 sm:text-base sm:leading-relaxed">{{ t("landing.pricing.lead") }}</p>
          </div>
          <div ref="priceCard" class="shrink-0 border-2 border-foreground bg-background px-4 py-3 text-left shadow-[var(--shadow-hard)] sm:px-6 sm:py-4 sm:text-right">
            <p ref="pricingAmount" class="font-mono text-3xl font-bold tabular-nums text-primary sm:text-5xl">{{ t("landing.pricing.amount") }}</p>
            <p class="mt-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-muted-foreground sm:mt-1.5 sm:text-[0.6875rem]">{{ t("landing.pricing.unit") }}</p>
            <p class="mt-2 border-t-2 border-foreground/10 pt-2 text-xs leading-relaxed text-muted-foreground sm:mt-3 sm:pt-3">{{ t("landing.pricing.example") }}</p>
          </div>
        </div>

        <!-- The fine print, given real space instead of a footnote: "what else do we
             pay?" and "can we get out?" are the two questions that follow a price, and
             leaving them to the FAQ meant leaving them unanswered for anyone who
             stopped scrolling here. -->
        <div ref="finePrint" class="mt-4 border-t-2 border-foreground/10 pt-4 sm:mt-10 sm:pt-8">
          <div class="grid gap-3 sm:grid-cols-2 sm:gap-8">
            <div>
              <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-primary sm:text-[0.6875rem]">{{ t("landing.pricing.fees_title") }}</p>
              <p class="mt-1 text-xs leading-snug text-muted-foreground sm:mt-1.5 sm:text-sm sm:leading-relaxed">{{ t("landing.pricing.fees_body") }}</p>
            </div>
            <div>
              <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-primary sm:text-[0.6875rem]">{{ t("landing.pricing.terms_title") }}</p>
              <p class="mt-1 text-xs leading-snug text-muted-foreground sm:mt-1.5 sm:text-sm sm:leading-relaxed">{{ t("landing.pricing.terms_body") }}</p>
            </div>
          </div>
          <!-- Direct contact routes, not a form: for a product handling a VvE's
               payments, being reachable in one click is the point. -->
          <p class="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:mt-6 sm:text-sm">
            {{ t("landing.pricing.managers_note") }}
            <a href="https://wa.me/31643500374" class="inline-flex items-center gap-1.5 font-semibold text-primary underline-offset-4 hover:underline">
              <MessageCircle class="size-3.5" />{{ t("landing.pricing.contact") }}
            </a>
          </p>
          <p class="mt-1 text-xs text-muted-foreground sm:text-sm">
            {{ t("landing.contact.hint") }}
            <a href="https://wa.me/31643500374" class="font-semibold text-primary underline-offset-4 hover:underline">{{ t("landing.contact.whatsapp") }}</a>
            · <a href="tel:+31643500374" class="font-semibold text-primary underline-offset-4 hover:underline">{{ t("landing.contact.call") }}</a>
            · <a href="mailto:info@pandig.nl" class="font-semibold text-primary underline-offset-4 hover:underline">{{ t("landing.contact.email") }}</a>
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
